import { createContext, useContext } from "react";
import type { SetStateAction } from "react";
import {
	type ProjectView,
	type ValidationReport,
} from "@kitsulab/schema";
import type {
	Attack,
	Currency,
	Folder,
	Item,
	Place,
	Project,
	Quest,
	Recipe,
	StatDefinition,
	UiTree,
} from "@/lib/types";

export type ProjectTheme = { tokens: Record<string, unknown> } | null;

export interface Bundle {
	project: Project | null;
	items: Item[];
	attacks: Attack[];
	recipes: Recipe[];
	currencies: Currency[];
	stats: StatDefinition[];
	quests: Quest[];
	folders: Folder[];
	places: Place[];
	config: Record<string, unknown>;
	uiTrees: UiTree[];
	theme: ProjectTheme;
}

export type EntityCollections = Pick<
	Bundle,
	| "items"
	| "attacks"
	| "recipes"
	| "currencies"
	| "stats"
	| "quests"
	| "places"
	| "uiTrees"
>;

export type EntityCollectionKey = keyof EntityCollections;

export type EntityFor<K extends EntityCollectionKey> = EntityCollections[K][number];
export type BundleLoadStatus = "loading" | "ready" | "not-found" | "error";

export interface ProjectData {
	slug: string;
	project: Project | null;
	items: Item[];
	attacks: Attack[];
	recipes: Recipe[];
	currencies: Currency[];
	stats: StatDefinition[];
	quests: Quest[];
	folders: Folder[];
	places: Place[];
	uiTrees: UiTree[];
	theme: ProjectTheme;
	config: Record<string, unknown>;
	report: ValidationReport;
	reload: () => Promise<void>;
	upsert: <K extends EntityCollectionKey>(
		kind: K,
		entity: EntityFor<K>,
	) => void;
	upsertMany: <K extends EntityCollectionKey>(
		kind: K,
		entities: EntityFor<K>[],
	) => void;
	remove: (kind: EntityCollectionKey, id: string) => void;
	setFolders: (folders: SetStateAction<Folder[]>) => void;
	setTheme: (theme: ProjectTheme) => void;
}

export const ProjectDataContext = createContext<ProjectData | null>(null);

export const toProjectView = (b: Bundle): ProjectView =>
	({
		items: Object.fromEntries(
			b.items.map((item) => [
				item.id,
				{
					id: item.id,
					name: item.name,
					modelPaths: item.modelPaths,
					category: item.category,
					maxQuantity: item.maxQuantity,
					wanderRadius: item.wanderRadius,
					minTeamsToStart: item.minTeamsToStart,
					maxTeams: item.maxTeams,
					allowedTeamSizes: item.allowedTeamSizes,
					countdownSeconds: item.countdownSeconds,
					matchQuest: item.matchQuest,
					stats: item.stats,
					attacks: item.attacks,
					graph: item.graph,
					folderId: item.folderId,
				},
			]),
		),
		attacks: Object.fromEntries(
			b.attacks.map((attack) => [
				attack.id,
				{
					id: attack.id,
					name: attack.name,
					range: attack.range,
					cooldown: attack.cooldown,
					animationId: attack.animationId,
					vfxModelPath: attack.vfxModelPath,
					aoeRadius: attack.aoeRadius,
					costStat: attack.costStat,
					costAmount: attack.costAmount,
					damage: attack.damage,
					hitStunDuration: attack.hitStunDuration,
					swingSoundId: attack.swingSoundId,
					impactSoundId: attack.impactSoundId,
					screenShakeIntensity: attack.screenShakeIntensity,
					projectileModelPath: attack.projectileModelPath,
					projectileSpeed: attack.projectileSpeed,
					projectileMaxDistance: attack.projectileMaxDistance,
					projectileHoming: attack.projectileHoming,
					projectilePiercing: attack.projectilePiercing,
					folderId: attack.folderId,
				},
			]),
		),
		recipes: Object.fromEntries(
			b.recipes.map((recipe) => [
				recipe.id,
				{
					id: recipe.id,
					name: recipe.name,
					inputs: recipe.inputs,
					outputs: recipe.outputs,
					developerProductId: recipe.developerProductId,
					description: recipe.description,
				},
			]),
		),
		currencies: Object.fromEntries(
			b.currencies.map((currency) => [
				currency.id,
				{
					id: currency.id,
					name: currency.name,
					modelPath: currency.modelPath,
					order: currency.order,
					passiveRatePerHour: currency.passiveRatePerHour,
					maxAmount: currency.maxAmount,
				},
			]),
		),
		stats: Object.fromEntries(
			b.stats.map((stat) => [
				stat.id,
				{
					id: stat.id,
					name: stat.name,
					order: stat.order,
					showInUi: stat.showInUi,
					display: stat.display,
					impact: stat.impact,
					iconAssetId: stat.iconAssetId,
					progression: stat.progression,
					showTierChangeMessage: stat.showTierChangeMessage,
					regenPerSecond: stat.regenPerSecond,
				},
			]),
		),
		quests: Object.fromEntries(
			b.quests.map((quest) => [
				quest.id,
				{
					id: quest.id,
					name: quest.name,
					description: quest.description,
					type: quest.type,
					winObjectives: quest.winObjectives,
					loseObjectives: quest.loseObjectives,
					rewards: quest.rewards,
					order: quest.order,
					timeLimit: quest.timeLimit,
					repeatable: quest.repeatable,
				},
			]),
		),
		places: Object.fromEntries(
			b.places.map((place) => [
				place.id,
				{
					id: place.id,
					kind: place.kind,
					label: place.label,
					path: place.path,
				},
			]),
		),
		uiTrees: Object.fromEntries(
			b.uiTrees.map((tree) => [
				tree.id,
				{
					id: tree.id,
					name: tree.name,
					role: tree.role,
					root: tree.root,
				},
			]),
		),
	}) as ProjectView;

export function useProjectData(): ProjectData {
	const ctx = useContext(ProjectDataContext);
	if (!ctx)
		throw new Error("useProjectData must be used inside ProjectDataProvider");
	return ctx;
}

export function useProjectDataOptional(): ProjectData | null {
	return useContext(ProjectDataContext);
}
